import {
  Autocomplete,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
} from '@mui/material'
import Drawer from '@mui/material/Drawer'
import { defaultDrawerSX } from 'const'
import {
  BaseTableRequest,
  Option,
  FilterModel,
  FilterModelText,
  FilterModelBoolean,
  FilterModelNumberArray,
} from 'types'
import DrawerContainer from '../common/DrawerContainer'
import DrawerHeader from '../common/DrawerHeader'
import { adminGlobalSearch } from 'api/adminSearch'
import columnRenderers from 'components/column-renderers'
import { ColDef, ICellRendererParams } from 'ag-grid-community'
import { SyntheticEvent, useCallback, useMemo, useState } from 'react'
import { routes } from 'routes/routes'
import InfinityAgGrid from 'components/table/InfinityAgGrid'
import { useGetDataSource } from 'utils'
import { useGridControl } from 'hooks/useGridControl'
import { CSVField, useExportCSV } from 'hooks/useExportCSV'
import moment from 'moment'
import TableToolbar from 'components/table/TableToolbar'
import { EAdminPostType } from 'types/admin.types'
import { formatDurationFromMinutes } from 'features/Metric/Metric.model'
import { useDateRange } from 'features/Reports/hooks/useDateRange'
import { prepareDateRangeParams } from 'features/Reports/helpers/prepareDateRangeParams'
import { DateRangeToolbar } from 'features/Reports/components/DateRangeToolbar/DateRangeToolbar'
import { getAdminsUsers } from 'api/users'
import { useQuery } from 'react-query'

interface Props {
  isOpen: boolean
  searchQuery: string
  handleClose: () => void
  searchFilters: FilterModel | null
}

type PublishFilter = boolean | string

const csvFields: CSVField[] = [
  { label: 'Name', value: 'name' },
  { label: 'Type', value: 'itemType' },
  { label: 'Talk Time', value: 'talkTime' },
]

const searchTypeFields: Option[] = [
  { label: 'Courses', value: EAdminPostType.course },
  { label: 'Lessons', value: EAdminPostType.lessons },
  { label: 'Learn Library Articles', value: EAdminPostType.learnArticles },
  { label: 'Action Posts', value: EAdminPostType.taskArticle },
  { label: 'Guide Posts', value: EAdminPostType.careArticle },
]

const searchPublishFields = [
  { label: 'All', value: 'all' },
  { label: 'Publish', value: true },
  { label: 'Unpublish', value: false },
]

const AdminSearchResultDrawer = (props: Props) => {
  const { isOpen, searchQuery, searchFilters, handleClose } = props
  const [contentTypeFilter, setContentTypeFilter] = useState<Option[]>([])
  const [adminsFilter, setAdminsFilter] = useState<Option[]>([])
  const [contentPublishFilter, setContentPublishFilter] = useState<PublishFilter>('all')

  const adminsUsersQuery = useQuery(['admins_users'], () => getAdminsUsers())

  const {
    rangeType,
    year,
    month,
    selectedDate,
    dateInterval,
    onChangeDateInterval,
    onChangeRange,
    onChangeYear,
    onChangeMonth,
    setSelectedDate,
  } = useDateRange()

  const getRequest = useCallback(
    (req: BaseTableRequest) => {
      const filter: FilterModel = { ...(req.filter || {}), ...(searchFilters || {}) }

      const dateParams = prepareDateRangeParams(rangeType, year, month, selectedDate, dateInterval)

      if (contentTypeFilter.length > 0) {
        filter.itemType = {
          type: 'text',
          op: 'in',
          value: contentTypeFilter.map((filter) => filter.value),
        } as FilterModelText
      }

      if (contentPublishFilter !== 'all') {
        filter.isPublished = {
          type: 'boolean',
          op: 'equals',
          value: contentPublishFilter,
        } as FilterModelBoolean
      }

      if (adminsFilter.length > 0) {
        filter.createdBy = {
          type: 'number',
          op: 'in',
          value: adminsFilter.map((filter) => filter.value),
        } as FilterModelNumberArray
      }

      const request = {
        ...req,
        filter,
        params: { searchText: searchQuery, ...dateParams },
      }

      return adminGlobalSearch(request).then((res) => res)
    },
    [
      adminsFilter,
      contentPublishFilter,
      contentTypeFilter,
      dateInterval,
      month,
      rangeType,
      searchFilters,
      searchQuery,
      selectedDate,
      year,
    ],
  )

  const getDataSource = useGetDataSource(getRequest)
  const { onGridReady, gridApi, columnApi } = useGridControl(getDataSource)

  const handleChangeContentTypeFilter = useCallback(
    (_: SyntheticEvent, newValue: Option[]) => {
      setContentTypeFilter(newValue)
      gridApi?.refreshServerSide()
    },
    [gridApi],
  )

  const handleChangeContentPublishFilter = useCallback(
    (e: SelectChangeEvent<PublishFilter>) => {
      setContentPublishFilter(e.target.value)
      gridApi?.refreshServerSide()
    },
    [gridApi],
  )

  const handleChangeAdminsFilter = useCallback(
    (_: SyntheticEvent, newValue: Option[]) => {
      setAdminsFilter(newValue)
      gridApi?.refreshServerSide()
    },
    [gridApi],
  )

  const columnDefs: ColDef[] = useMemo(() => {
    return [
      {
        headerName: 'Name',
        cellRenderer: columnRenderers.createLink('name', routes.tipEdit),
        colId: 'name',
      },
      {
        headerName: 'Type',
        colId: 'itemType',
        cellRenderer: (props: ICellRendererParams<{ [field: string]: EAdminPostType }>) => {
          const value = props.data?.itemType
          return searchTypeFields.find((i) => i.value === value)?.label || ''
        },
      },
      {
        headerName: 'Talk Time',
        colId: 'talkTime',
        cellRenderer: (props: ICellRendererParams<{ [field: string]: number }>) => {
          const value = props.data?.talkTime || 0
          return formatDurationFromMinutes(value)
        },
      },
    ]
  }, [])

  const adminUsersOptions: Option[] = useMemo(
    () =>
      adminsUsersQuery.data
        ? adminsUsersQuery.data.data.rows.map((user) => ({
            value: user.id,
            label: `${user.firstname} ${user.lastname}`,
          }))
        : [],
    [adminsUsersQuery.data],
  )

  const { csvLoading, onExportCSV } = useExportCSV(
    getRequest,
    gridApi,
    columnApi,
    csvFields,
    `Admin_Search_Overview_${moment().format('YYYY-MM-DD')}.csv`,
  )

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={handleClose}
      sx={defaultDrawerSX}
      disableEnforceFocus
    >
      {isOpen && (
        <DrawerContainer isOpen={isOpen} hasData loading={false} handleClose={handleClose}>
          <DrawerHeader handleClose={handleClose}>Search results:</DrawerHeader>
          <TableToolbar
            left={
              <Stack direction="row" spacing={1} flex="1">
                <FormControl sx={{ flexGrow: 1 }} size="small">
                  <Autocomplete
                    multiple
                    options={searchTypeFields}
                    value={contentTypeFilter}
                    onChange={handleChangeContentTypeFilter}
                    openOnFocus
                    fullWidth
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        InputProps={{ ...params.InputProps }}
                        placeholder="Content Type"
                      />
                    )}
                  />
                </FormControl>
                <FormControl sx={{ flexGrow: 1 }} size="small">
                  <InputLabel>Published</InputLabel>
                  <Select
                    value={contentPublishFilter}
                    label="Published"
                    onChange={handleChangeContentPublishFilter}
                  >
                    {searchPublishFields.map((field) => (
                      <MenuItem key={field.label} value={field.value.toString()}>
                        {field.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            }
            exportCSVEnable
            csvLoading={csvLoading}
            onExportCSV={onExportCSV}
          />

          <Stack direction="row" mb={2} flex="1">
            <DateRangeToolbar
              rangeType={rangeType}
              year={year}
              month={month}
              dateInterval={dateInterval}
              selectedDate={selectedDate}
              onChangeDateInterval={onChangeDateInterval}
              onChangeRange={onChangeRange}
              onChangeYear={onChangeYear}
              onChangeMonth={onChangeMonth}
              setSelectedDate={setSelectedDate}
            />

            <Box minWidth="200px" marginLeft={2}>
              <Autocomplete
                multiple
                options={adminUsersOptions}
                value={adminsFilter}
                onChange={handleChangeAdminsFilter}
                openOnFocus
                fullWidth
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    InputProps={{ ...params.InputProps }}
                    placeholder="Created By"
                  />
                )}
              />
            </Box>
          </Stack>

          <Box className="ag-theme-alpine" height="100%">
            <InfinityAgGrid pagination columnDefs={columnDefs} onGridReady={onGridReady} />
          </Box>
        </DrawerContainer>
      )}
    </Drawer>
  )
}

export default AdminSearchResultDrawer
